import clsx from 'clsx';
import { memo } from 'react';
import { getStoreHoursText } from '~/features/find-store/components/StoreMap/utils/get-store-hours-text';
import { SalesforceProduct } from '~/lib/salesforce/types';
import Spinner from '~/shared/components/Spinner/Spinner';
import { Typography } from '~/shared/components';
import { useTranslation } from '~/shared/utils/translation';
import {
    StoreDetails,
    StoreDetailsPanel,
} from '~/features/product-details/components/ReservationDrawer/StoreDetails';
import { StoreAddress } from '~/features/product-details/components/ReservationDrawer/StoreAddress';
import {
    PDPStoreListLi,
    StyledButton,
} from '~/features/product-details/components/ReservationDrawer/styled';
import type {
    ReservationStoreBase,
    ReservationStoreCtaIntent,
} from '~/features/product-details/components/ReservationDrawer/types';
import { getStoreOpeningHoursWithStatus } from '~/features/product-details/components/ReservationDrawer/utils/getStoreOpeningHoursWithStatus';
import { mapStoreToReservationStore } from '~/features/product-details/components/ReservationDrawer/utils/mapStoreToReservationStore';
import {
    canReserveFromStore,
    getStoreReservationStatus,
} from '~/features/product-details/components/ReservationDrawer/utils/reservationStoreStatus';
import styles from './ReservationStoreList.module.scss';

type Props = {
    store: ReservationStoreBase;
    product: SalesforceProduct;
    isStoreCtaPending: boolean;
    pendingStoreCtaId: string | null;
    onStoreCtaClick: (intent: ReservationStoreCtaIntent) => void;
    onAccordionOpen: (shopId: string, index: number) => void;
    index: number;
    quantity: number;
};

// Memoized so a search (which reorders the same store objects) reconciles existing rows by their
// stable `store.id` key instead of re-rendering all ~165 rows.
export const ReservationStoreListItem = memo(
    ({
        store,
        product,
        isStoreCtaPending,
        pendingStoreCtaId,
        quantity,
        onStoreCtaClick,
        onAccordionOpen,
        index,
    }: Props) => {
        const { translate } = useTranslation();
        const stockLevel = store.c_stock_level ?? 0;
        const hasStoreStock = stockLevel >= 1;

        const canReserve = canReserveFromStore(store);

        const reservationStatus = getStoreReservationStatus({
            store,
            product,
            quantity,
            translate,
        });
        const openingHours = getStoreOpeningHoursWithStatus(store.c_openingHours);
        const storeIsOpen = openingHours[0]?.isOpen;

        const handleChooseStore = () => {
            const reservationStore = mapStoreToReservationStore(store, {
                closedLabel: translate('pdp.closedStore'),
                openLabel: translate('pdp.openStore'),
                labelStoreHoursText: getStoreHoursText(translate, openingHours, store),
                storeIsOpen,
            });

            onStoreCtaClick({
                type: reservationStatus.opensMinibasket
                    ? 'addToMinibasketWithStoreSuggestion'
                    : 'reserveInStore',
                store: reservationStore,
            });
        };

        const isCurrentMinibasketCtaPending =
            reservationStatus.opensMinibasket && pendingStoreCtaId === store.id;

        return (
            <PDPStoreListLi className={styles.storeItem}>
                <div className={styles.storeContent}>
                    <strong className={clsx(!reservationStatus.showCta && styles.dimmed)}>
                        {store.name || ''}
                    </strong>
                    <Typography
                        className={clsx(
                            styles.storeStock,
                            !reservationStatus.showCta && styles.dimmed,
                        )}
                        variant="paragraph-XS"
                    >
                        {`${stockLevel} på lager`}
                        {hasStoreStock &&
                            !canReserve &&
                            ` (${translate('pdp.cannotReserveInShop')})`}
                    </Typography>
                    <Typography
                        className={clsx(styles.storeStatus, styles[reservationStatus.tone])}
                        variant="paragraph-XS"
                    >
                        {reservationStatus.text}
                    </Typography>
                </div>
                <div className={styles.storeActions}>
                    {reservationStatus.showCta && (
                        <StyledButton
                            aria-label={reservationStatus.buttonText}
                            type="submit"
                            disabled={isStoreCtaPending}
                            onClick={handleChooseStore}
                            size="small"
                            small
                            variant={
                                reservationStatus.opensMinibasket ? 'pdp-secondary' : undefined
                            }
                        >
                            {reservationStatus.buttonText}
                            {isCurrentMinibasketCtaPending && <Spinner />}
                        </StyledButton>
                    )}
                </div>
                <StoreDetails
                    onOpen={() => onAccordionOpen(store.id, index)}
                    showAddressTranslation
                >
                    <StoreDetailsPanel store={store} openingHours={openingHours}>
                        <StoreAddress store={store} />
                    </StoreDetailsPanel>
                </StoreDetails>
            </PDPStoreListLi>
        );
    },
);
ReservationStoreListItem.displayName = 'ReservationStoreListItem';
